/**
 * `/settings/integrations` — the Google connection.
 *
 * SELF-SERVICE for every role, which is why this page sits under /settings but
 * is NOT ADMIN-gated: a Google connection is one person's own calendar, and an
 * admin connecting one on somebody's behalf would mean holding their
 * credentials. The scheduling service refuses any user id but the caller's, so
 * the boundary is real rather than a UI convention.
 *
 * When the deployment has no OAuth client, this page says so and explains what
 * an operator must do — the whole feature degrades to a notice rather than a
 * broken button.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button, Spinner } from '@meshhub/design-tokens/ui';
import { AlertTriangle, Link2, RefreshCw, Unlink } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConfirmDialog, MutationError, TextField } from '../../components/FormKit.web.jsx';
import { getAccessToken } from '../../lib/auth.jsx';
import { formatDateTime } from '../../lib/format.js';
import {
  DISCONNECT_GOOGLE,
  GOOGLE_CONNECTION_QUERY,
  SET_GOOGLE_CALENDAR,
  SYNC_GOOGLE_NOW,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

/**
 * Attach the in-memory access token to a route the browser NAVIGATES to.
 *
 * Returns '#' when the server offered no URL — Google is not configured — so
 * the link is inert rather than a navigation to a token-bearing 503.
 */
export function withToken(url) {
  if (!url) return '#';
  const token = getAccessToken();
  if (!token) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}token=${encodeURIComponent(token)}`;
}

export function IntegrationsScreen() {
  const [params, setParams] = useSearchParams();
  const [notice, setNotice] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [calendarId, setCalendarId] = useState('');

  const { data, loading, error, refetch } = useQuery(GOOGLE_CONNECTION_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [syncNow, syncState] = useMutation(SYNC_GOOGLE_NOW);
  const [disconnect, disconnectState] = useMutation(DISCONNECT_GOOGLE);
  const [setCalendar, calendarState] = useMutation(SET_GOOGLE_CALENDAR);

  const connection = data?.googleConnection;

  useEffect(() => {
    if (connection?.calendarId) setCalendarId(connection.calendarId);
  }, [connection?.calendarId]);

  /**
   * The OAuth callback sends the browser back here with an outcome in the query
   * string. Read once and cleared, so a reload does not re-announce a
   * connection that happened ten minutes ago.
   */
  useEffect(() => {
    const outcome = params.get('google');
    if (!outcome) return;
    const detail = params.get('detail');
    setNotice(
      outcome === 'connected'
        ? { kind: 'ok', text: S.integrations.connected }
        : outcome === 'denied'
          ? { kind: 'warn', text: S.integrations.denied }
          : { kind: 'error', text: `${S.integrations.error}: ${detail ?? ''}` },
    );
    setParams({}, { replace: true });
    refetch();
  }, [params, setParams, refetch]);

  if (loading && !connection) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-5">
      <header>
        <h1 className="text-md font-bold">{S.integrations.title}</h1>
      </header>

      <MutationError
        error={error ?? syncState.error ?? disconnectState.error ?? calendarState.error}
      />

      {notice && (
        <p
          className={[
            'rounded border px-3 py-2 text-sm',
            notice.kind === 'ok'
              ? 'border-success text-success'
              : notice.kind === 'warn'
                ? 'border-warning text-warning'
                : 'border-danger text-danger',
          ].join(' ')}
        >
          {notice.text}
        </p>
      )}

      <section className="flex flex-col gap-4 rounded border border-border bg-surface p-5">
        <h2 className="text-base font-medium">{S.integrations.google}</h2>

        {!connection?.configured ? (
          /* Not an error — a deployment fact. The page explains what an
             operator must do instead of showing a button that cannot work. */
          <div className="flex items-start gap-3 rounded border border-border bg-bg p-4">
            <AlertTriangle size={16} className="mt-px text-warning" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">{S.integrations.notConfigured}</span>
              <p className="text-sm text-text-muted">{S.integrations.notConfiguredHint}</p>
            </div>
          </div>
        ) : connection.connected ? (
          <>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <dt className="text-text-muted">{S.integrations.connectedAs}</dt>
              <dd dir="ltr" className="text-start">
                {connection.googleEmail || S.app.none}
              </dd>
              <dt className="text-text-muted">{S.integrations.lastSync}</dt>
              <dd>
                {connection.lastSyncAt
                  ? formatDateTime(connection.lastSyncAt)
                  : S.integrations.never}
              </dd>
              {connection.lastError && (
                <>
                  <dt className="text-text-muted">{S.integrations.lastError}</dt>
                  <dd className="text-danger">{connection.lastError}</dd>
                </>
              )}
            </dl>

            <div className="flex items-end gap-2">
              <TextField
                label={S.integrations.calendarTarget}
                value={calendarId}
                onChange={(event) => setCalendarId(event.target.value)}
                dir="ltr"
              />
              <Button
                type="button"
                disabled={!calendarId || calendarId === connection.calendarId}
                onClick={() => setCalendar({ variables: { calendarId } })}
              >
                {S.app.save}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="primary"
                disabled={syncState.loading}
                onClick={async () => {
                  const result = await syncNow();
                  const outcome = result.data?.syncGoogleNow;
                  if (outcome) {
                    setNotice({
                      kind: outcome.errors?.length ? 'warn' : 'ok',
                      text:
                        outcome.skippedReason ||
                        S.integrations.syncResult(
                          outcome.pushed,
                          outcome.pulled,
                          outcome.imported,
                        ),
                    });
                  }
                }}
              >
                <RefreshCw size={14} aria-hidden="true" />
                {syncState.loading ? S.integrations.syncing : S.integrations.syncNow}
              </Button>
              <Button type="button" onClick={() => setConfirmDisconnect(true)}>
                <Unlink size={14} aria-hidden="true" />
                {S.integrations.disconnect}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-start gap-3">
            {connection.googleEmail && (
              // The row remembers WHICH account was connected, so a revoked
              // connection can say "reconnect this one" rather than starting
              // from nothing.
              <p className="text-sm text-text-muted">
                {S.integrations.connectedAs}: <span dir="ltr">{connection.googleEmail}</span>
              </p>
            )}
            {connection.lastError && (
              <p className="text-sm text-danger">{connection.lastError}</p>
            )}
            {/* A plain link, not a fetch: OAuth is a top-level browser
                navigation, and an XHR to it would be blocked by Google.
                Which is also why the token rides in the QUERY STRING — a
                navigation carries no Authorization header, and the access
                token lives in memory rather than localStorage, so there is
                nothing for the browser to attach. Same trade as the CSV
                export link and the pairing stream. */}
            <a
              href={withToken(connection.connectUrl)}
              className="flex h-control items-center gap-2 rounded bg-primary px-4 text-sm
                         text-primary-contrast hover:bg-primary-hover"
            >
              <Link2 size={14} aria-hidden="true" />
              {connection.googleEmail ? S.integrations.reconnect : S.integrations.connect}
            </a>
          </div>
        )}

        <details className="text-sm">
          <summary className="cursor-pointer text-text-muted">
            {S.integrations.whatSyncs}
          </summary>
          <p className="mt-2 leading-relaxed text-text-muted">
            {S.integrations.whatSyncsBody}
          </p>
        </details>
      </section>

      {connection?.recent?.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-medium">{S.integrations.recentActivity}</h2>
          <table className="w-full border-collapse text-sm">
            <tbody>
              {connection.recent.map((entry, index) => (
                <tr key={`${entry.at}-${index}`} className="border-b border-border">
                  <td className="px-4 py-2 align-middle text-text-muted">
                    {formatDateTime(entry.at)}
                  </td>
                  <td className="px-4 py-2 align-middle" dir="ltr">
                    {entry.direction}
                  </td>
                  <td className="px-4 py-2 align-middle" dir="ltr">
                    {entry.entity}
                  </td>
                  <td
                    className={`px-4 py-2 align-middle ${
                      entry.outcome === 'error'
                        ? 'text-danger'
                        : entry.outcome === 'conflict'
                          ? 'text-warning'
                          : ''
                    }`}
                    dir="ltr"
                  >
                    {entry.outcome}
                  </td>
                  <td className="px-4 py-2 align-middle text-text-muted">{entry.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <ConfirmDialog
        open={confirmDisconnect}
        onOpenChange={setConfirmDisconnect}
        title={S.integrations.disconnect}
        description={S.integrations.disconnectConfirm}
        confirmLabel={S.integrations.disconnect}
        destructive
        onConfirm={async () => {
          await disconnect();
          setConfirmDisconnect(false);
          refetch();
        }}
      />
    </div>
  );
}
